/**
 * Journey B — "Your recent proposals" tracker.
 *
 * Analysts (and managers/leads) can PROPOSE changes but an analyst can't
 * approve their own — so they need a way to SEE what they submitted and where
 * it stands. This card surfaces the CURRENT user's own proposals across every
 * status (pending / approved / denied / executing / done), newest first, each
 * with the tool, shop, a status pill, and an age. "View all" deep-links to the
 * proposals queue.
 *
 * Scoping to self:
 *   The employee-scoped proposals API accepts `requested_by_employee_id`, so we
 *   pass the current employee's id (`auth.agency.employeeIdentity.employee_id`)
 *   as a server-side filter. As a belt-and-braces guard — in case a backend
 *   build ignores the param — we ALSO filter the returned rows client-side to
 *   `requested_by_employee_id === myId`. If we have no employee id at all, the
 *   card hides itself rather than showing other people's proposals.
 *
 * Gating:
 *   Rendered behind `proposals.propose` (owner / team_lead / manager / analyst)
 *   so read-only members — who can't submit anything — never see it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SimpleCard from '../../ui/cards/SimpleCard';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import { tickWithCircleSVG } from '../../lib/allCustomSVGs';
import { RootState } from '../../store/store';
import {
  Proposal,
  ProposalStatus,
  listProposals,
} from '../../lib/api/proposals/proposals-api';
import '../../styles/pages/dashboard/dashboardAgencyCards.scss';

const PLATFORM_LABELS: Record<string, string> = {
  facebook_ads: 'Facebook Ads',
  facebook: 'Facebook Ads',
  google_ads: 'Google Ads',
  tiktok_ads: 'TikTok Ads',
  tiktok: 'TikTok Ads',
  linkedin_ads: 'LinkedIn Ads',
  linkedin: 'LinkedIn Ads',
  ga4: 'GA4',
  gsc: 'Search Console',
};

const platformName = (p: string): string =>
  PLATFORM_LABELS[p] || (p ? p.replace(/_/g, ' ') : '—');

const toolLabelOf = (p: Proposal): string => {
  const raw = p.tool_name || p.action_type?.name || p.action_type_id || '';
  return raw
    ? raw.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    : 'Action';
};

// Map the raw API status onto a small set of pill "tones" + i18n labels so the
// status reads consistently regardless of the exact backend string.
type StatusTone = 'pending' | 'approved' | 'denied' | 'executing' | 'done';

const STATUS_TONE: Record<ProposalStatus, StatusTone> = {
  proposed: 'pending',
  pending_review: 'pending',
  approved: 'approved',
  denied: 'denied',
  executing: 'executing',
  completed: 'done',
  failed: 'denied',
};

const STATUS_LABEL_KEY: Record<StatusTone, string> = {
  pending: 'MyProposalsStatusPending',
  approved: 'MyProposalsStatusApproved',
  denied: 'MyProposalsStatusDenied',
  executing: 'MyProposalsStatusExecuting',
  done: 'MyProposalsStatusDone',
};

const MAX_ROWS = 5;

const MyProposalsCard = () => {
  const { t } = useTranslation();
  const myEmployeeId = useSelector(
    (s: RootState) => s.auth.agency.employeeIdentity?.employee_id
  );

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!myEmployeeId) {
      // No identity to scope by — don't fetch (and the card hides anyway).
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const res = await listProposals({
        requested_by_employee_id: myEmployeeId,
        per_page: MAX_ROWS,
        page: 1,
      });

      // Belt-and-braces: re-filter client-side in case the backend ignored the
      // `requested_by_employee_id` param so we never surface someone else's row.
      const mine = (res.data || []).filter(
        (p) =>
          !p.requested_by_employee_id ||
          p.requested_by_employee_id === myEmployeeId
      );
      const sorted = mine.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setProposals(sorted.slice(0, MAX_ROWS));
      // Prefer the server total when the filter was honoured; otherwise fall
      // back to what we actually kept after the client-side guard.
      const serverTotal = res.meta?.total ?? res.data?.length ?? 0;
      setTotal(
        mine.length === (res.data?.length ?? 0) ? serverTotal : mine.length
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[MyProposalsCard] load failed', err);
      setError(true);
      setProposals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [myEmployeeId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const formatAge = (createdAt: string): string => {
    const created = new Date(createdAt);
    const ageMin = Math.max(
      0,
      Math.round((Date.now() - created.getTime()) / 60000)
    );
    if (ageMin < 60) return `${ageMin}m ago`;
    if (ageMin < 24 * 60) return `${Math.round(ageMin / 60)}h ago`;
    return `${Math.round(ageMin / (60 * 24))}d ago`;
  };

  // Without an employee identity we can't scope to "mine" — hide entirely
  // rather than risk showing the whole org's proposals.
  if (!myEmployeeId) return null;

  return (
    <SimpleCard grow={false}>
      <div className='pendingProposalsCard'>
        <div className='pendingProposalsCard__header'>
          <div className='pendingProposalsCard__header-title'>
            <h3>{t('MyProposalsTitle')}</h3>
            {total > 0 && (
              <span className='pendingProposalsCard__header-title-badge'>
                {total}
              </span>
            )}
          </div>
          <Link
            to='/actions/queue'
            className='pendingProposalsCard__header-link'
          >
            {t('MyProposalsViewAll')}
          </Link>
        </div>

        {loading && (
          <div className='pendingProposalsCard__loading'>
            <LoadingSpinner />
          </div>
        )}

        {!loading && error && (
          <div className='pendingProposalsCard__empty'>
            {t('MyProposalsLoadError')}
          </div>
        )}

        {!loading && !error && proposals.length === 0 && (
          <div className='pendingProposalsCard__caughtUp'>
            <span
              className='pendingProposalsCard__caughtUp-icon'
              aria-hidden='true'
            >
              <CustomSVGs svg={tickWithCircleSVG} />
            </span>
            <span className='pendingProposalsCard__caughtUp-text'>
              {t('MyProposalsEmpty')}
            </span>
          </div>
        )}

        {!loading && !error && proposals.length > 0 && (
          <div className='pendingProposalsCard__list'>
            {proposals.map((p) => {
              const shopLabel =
                p.shop?.name || p.shop?.slug || `shop #${p.shop_id}`;
              const tone: StatusTone =
                STATUS_TONE[p.status as ProposalStatus] || 'pending';
              return (
                <div key={p.id} className='pendingProposalsCard__row'>
                  <div className='pendingProposalsCard__row-main'>
                    <div className='pendingProposalsCard__row-title'>
                      <span>{toolLabelOf(p)}</span>
                      <span className={`status-pill status-pill--${tone}`}>
                        {t(STATUS_LABEL_KEY[tone])}
                      </span>
                    </div>
                    <div className='pendingProposalsCard__row-meta'>
                      {shopLabel} · {platformName(p.platform)} ·{' '}
                      {formatAge(p.created_at)}
                    </div>
                  </div>
                  <div className='pendingProposalsCard__row-actions'>
                    <Link
                      to='/actions/queue'
                      className='pendingProposalsCard__btn pendingProposalsCard__btn--review'
                    >
                      {t('View')}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SimpleCard>
  );
};

export default MyProposalsCard;
